import React from 'react';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  FileText,
  Network,
  Activity,
  Shield,
  FileDown,
  TrendingUp,
  Clock,
  X,
  Sliders,
} from 'lucide-react';
import { ROLE_LABELS } from '../../data/mockData';
import { toast } from 'sonner';

const roleColors = {
  admin: 'bg-red-50 text-red-700 border-red-200',
  supreme_manager: 'bg-purple-50 text-purple-700 border-purple-200',
  manager: 'bg-blue-50 text-blue-700 border-blue-200',
  quality_checker: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  field_executive: 'bg-amber-50 text-amber-700 border-amber-200',
  vendor: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  client: 'bg-pink-50 text-pink-700 border-pink-200',
  business_partner: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  universal_qc: 'bg-teal-50 text-teal-700 border-teal-200',
  data_entry_operator: 'bg-orange-50 text-orange-700 border-orange-200',
  vendor_fe: 'bg-sky-50 text-sky-700 border-sky-200',
};

export default function UserDossierView({
  activeDossierUser,
  setActiveDossierUser,
  activeDossierTab,
  setActiveDossierTab,
  users,
  permissionsMatrix,
  handleTogglePermission,
}) {
  return (
    <Dialog open={!!activeDossierUser} onOpenChange={(open) => !open && setActiveDossierUser(null)}>
      <DialogContent className="bg-white border-slate-250 max-w-4xl p-0 overflow-hidden rounded-xl shadow-xl">
        {activeDossierUser && (
          <div className="flex flex-col h-[85vh]">
            {/* Dossier Header Info */}
            <div className="bg-slate-900 text-white p-6 relative flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16 ring-4 ring-white/10">
                  <AvatarImage src={activeDossierUser.avatar} />
                  <AvatarFallback className="bg-slate-800 text-white text-lg font-bold">{activeDossierUser.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-bold tracking-tight">{activeDossierUser.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded font-mono uppercase">
                      {ROLE_LABELS[activeDossierUser.role] || activeDossierUser.role}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${activeDossierUser.status === 'active' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}>
                      {activeDossierUser.status.toUpperCase()}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">Employee ID: {activeDossierUser.employeeId}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-3 text-xs md:text-right">
                <div>
                  <p className="text-white/40 text-[9px] uppercase tracking-wider font-semibold">Hierarchy Binding</p>
                  <p className="text-white font-bold mt-0.5">Parent: {activeDossierUser.parentName || 'System'}</p>
                </div>
              </div>

              <Button variant="ghost" size="icon" className="absolute top-3 right-3 text-white/50 hover:text-white" onClick={() => setActiveDossierUser(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* dossier Tabs Selector */}
            <div className="border-b border-slate-200 bg-slate-50 px-4">
              <div className="flex gap-1 overflow-x-auto whitespace-nowrap scrollbar-none py-1.5">
                {[
                  { id: 'basic', label: 'Basic Info', icon: FileText },
                  { id: 'hierarchy', label: 'Hierarchy', icon: Network },
                  { id: 'cases', label: 'Assigned Cases', icon: Sliders },
                  { id: 'logs', label: 'Activity Logs', icon: Activity },
                  { id: 'permissions', label: 'Permissions Checkers', icon: Shield },
                  { id: 'documents', label: 'Documents Drawer', icon: FileDown },
                  { id: 'performance', label: 'Performance TAT', icon: TrendingUp },
                ].map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveDossierTab(tab.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition ${activeDossierTab === tab.id ? 'bg-white text-blue-600 shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/50'}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dossier Tabs Content */}
            <ScrollArea className="flex-1 p-6">
              
              {/* 1. BASIC INFORMATION */}
              {activeDossierTab === 'basic' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">Contact Details</h4>
                      
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-slate-400 block">Email ID:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5">{activeDossierUser.email}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Contact Phone:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5">{activeDossierUser.phone}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Emergency Phone:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5">{activeDossierUser.emergencyPhone || '+91 99887 76655'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">System Username:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5 font-mono">{activeDossierUser.username}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">Employment Info</h4>
                      
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-slate-400 block">Date Of Joining:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5">{activeDossierUser.doj || activeDossierUser.joinDate}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Date Of Birth:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5">{activeDossierUser.dob || '1992-06-15'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Assigned Region:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5">{activeDossierUser.region || 'North'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Parent Creator:</span>
                          <span className="font-semibold text-slate-800 block mt-0.5">{activeDossierUser.parentName || 'System'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 border-t border-slate-100 pt-4 text-xs">
                    <span className="text-slate-400 block">Permanent Address:</span>
                    <p className="text-slate-800 font-semibold">{activeDossierUser.address || 'Sector 5, Salt Lake, Kolkata, India'}</p>
                  </div>
                </div>
              )}

              {/* 2. HIERARCHY DOWNLINE */}
              {activeDossierTab === 'hierarchy' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">Hierarchy Downline Tree Nodes</h4>
                  
                  {users.filter(u => u.parentId === activeDossierUser.id).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-6">No direct downline subordinates mapped to this user node.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {users.filter(u => u.parentId === activeDossierUser.id).map(sub => (
                        <div key={sub.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50 flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={sub.avatar} />
                            <AvatarFallback className="bg-slate-200 text-slate-600">{sub.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-slate-800 truncate">{sub.name}</p>
                            <span className={`inline-block px-1.5 py-0.5 text-[8px] font-bold border rounded ${roleColors[sub.role] || 'bg-slate-100 text-slate-600'}`}>{ROLE_LABELS[sub.role] || sub.role}</span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-mono">{sub.employeeId}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 3. ASSIGNED CASES */}
              {activeDossierTab === 'cases' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">Assigned Operational Cases (Simulation)</h4>
                  
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-left border-collapse text-[10px]">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                          <th className="px-3 py-2">Case Reference</th>
                          <th className="px-3 py-2">Applicant Name</th>
                          <th className="px-3 py-2">Verification Type</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Progress</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-600">
                        <tr className="hover:bg-slate-50/50">
                          <td className="px-3 py-2 font-mono font-bold text-slate-700">ADV-2026-09887</td>
                          <td className="px-3 py-2 font-semibold">Tushar Kanti Sen</td>
                          <td className="px-3 py-2 font-medium">Physical Address</td>
                          <td className="px-3 py-2">
                            <span className="bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded text-[8px] font-bold">IN PROGRESS</span>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">45%</td>
                        </tr>
                        <tr className="hover:bg-slate-50/50">
                          <td className="px-3 py-2 font-mono font-bold text-slate-700">ADV-2026-09452</td>
                          <td className="px-3 py-2 font-semibold">Ranjan Mullick</td>
                          <td className="px-3 py-2 font-medium">Employment Checks</td>
                          <td className="px-3 py-2">
                            <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded text-[8px] font-bold">COMPLETED</span>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">100%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 4. ACTIVITY LOGS */}
              {activeDossierTab === 'logs' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">User Operations History Logs</h4>
                  
                  <div className="space-y-3 text-xs">
                    <div className="flex gap-3">
                      <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-800">Forced Password Reset Window</p>
                        <span className="text-[10px] text-slate-400 block mt-0.5">2026-05-20 11:22:38 - Triggered via Admin console</span>
                      </div>
                    </div>
                    <div className="flex gap-3 border-t border-slate-100 pt-3">
                      <Activity className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-800">Active Profile Dossier Canvas view</p>
                        <span className="text-[10px] text-slate-400 block mt-0.5">2026-05-20 11:10:44 - Opened biography information</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 5. PERMISSIONS OVERRIDES */}
              {activeDossierTab === 'permissions' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">Custom User Override permissions</h4>
                  
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {[
                      { key: 'create', label: 'Allow User Additions' },
                      { key: 'edit', label: 'Allow User Detail Edits' },
                      { key: 'delete', label: 'Allow Deletions/Disables' },
                      { key: 'export', label: 'Allow Hierarchical Exports' },
                    ].map(perm => (
                      <label key={perm.key} className="border border-slate-200 rounded-lg p-3 bg-slate-50 flex items-center justify-between cursor-pointer hover:bg-slate-100/50 font-medium text-slate-700">
                        <span>{perm.label}</span>
                        <input
                          type="checkbox"
                          checked={permissionsMatrix[activeDossierUser.role]?.[perm.key] || false}
                          onChange={() => handleTogglePermission(activeDossierUser.role, perm.key)}
                          className="w-4 h-4 text-blue-600 rounded border-slate-300"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 6. DOCUMENTS DRAWER */}
              {activeDossierTab === 'documents' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">Uploaded Dossier Documents</h4>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="border border-slate-200 rounded-xl p-3 flex items-center justify-between bg-slate-50">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-bold text-slate-800 truncate">Identity_Proof.pdf</p>
                          <span className="text-[9px] text-slate-400">PDF Document • 4.2 MB</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => toast.success('Downloading document identity proof...')}>
                        <FileDown className="w-4 h-4 text-slate-500" />
                      </Button>
                    </div>

                    <div className="border border-slate-200 rounded-xl p-3 flex items-center justify-between bg-slate-50">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-5 h-5 text-purple-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-bold text-slate-800 truncate">Employment_Agreement.pdf</p>
                          <span className="text-[9px] text-slate-400">PDF Document • 12.8 MB</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => toast.success('Downloading agreement contract...')}>
                        <FileDown className="w-4 h-4 text-slate-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* 7. PERFORMANCE TAT */}
              {activeDossierTab === 'performance' && (
                <div className="space-y-6">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100">SLA Turnaround Time (TAT) Scorecard</h4>
                  
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Average TAT</span>
                      <strong className="text-2xl font-bold text-blue-600 block mt-1">24.5 Hours</strong>
                      <span className="text-[9px] text-emerald-600 font-medium block mt-1">▲ 12% faster than SLA</span>
                    </div>
                    <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Active Loads</span>
                      <strong className="text-2xl font-bold text-indigo-600 block mt-1">12 Leads</strong>
                      <span className="text-[9px] text-slate-400 block mt-1">Optimal allocation limit</span>
                    </div>
                    <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-4">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">SLA Breaches</span>
                      <strong className="text-2xl font-bold text-rose-600 block mt-1">1 Case</strong>
                      <span className="text-[9px] text-rose-500 block mt-1">98.2% compliance rate</span>
                    </div>
                  </div>
                </div>
              )}

            </ScrollArea>
            <div className="bg-slate-50 p-4 border-t border-slate-200 flex justify-end">
              <Button className="bg-slate-800 hover:bg-slate-900 text-white" onClick={() => setActiveDossierUser(null)}>
                Close Dossier
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
